import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './GroupsPage.css';

const benefits = [
  { icon: 'bi-shield-check', title: 'Zero Admin Burden', desc: 'We handle enrollment, billing, and member support. Your HR team stays focused.' },
  { icon: 'bi-graph-up', title: 'Revenue Potential', desc: 'Unlock new revenue through our partnership model. Healthcare that pays for itself.' },
  { icon: 'bi-people-fill', title: 'Retention Tool', desc: 'Employees who use their benefits stay longer. Oval Access+ drives real engagement.' },
  { icon: 'bi-patch-check-fill', title: 'Full Compliance', desc: 'HIPAA-compliant, LegitScript certified, state-licensed providers.' },
];

const offerModels = [
  { title: 'Member-Paid Perk', value: 'member-paid', desc: 'Offer Oval Access+ as a voluntary benefit. Employees pay directly—zero cost to your company.', highlights: ['No employer cost', 'Payroll deduction optional', 'Immediate availability'], icon: 'bi-gift' },
  { title: 'Sponsored Subsidy', value: 'sponsored', desc: 'Subsidize part or all of the membership. Show employees you invest in their wellness.', highlights: ['Partial or full sponsorship', 'Tax-advantaged benefit', 'Maximum engagement'], featured: true, icon: 'bi-heart-fill' },
  { title: 'Revenue Share', value: 'revenue-share', desc: 'Earn a share of enrollment revenue. Turn your benefits package into a profit center.', highlights: ['Passive income stream', 'Per-member commission', 'Quarterly payouts'], icon: 'bi-bar-chart-fill' },
];

const employeeBenefits = [
  { icon: 'bi-lightning-charge', label: 'GLP-1 Weight Loss', color: '#FF7A00' },
  { icon: 'bi-emoji-smile', label: 'Mental Health', color: '#4B9FE3' },
  { icon: 'bi-heart', label: 'Erectile Dysfunction', color: '#E84393' },
  { icon: 'bi-scissors', label: 'Hair Loss', color: '#00B894' },
  { icon: 'bi-droplet', label: 'Skin Care', color: '#A29BFE' },
  { icon: 'bi-moon', label: 'Sleep & Anxiety', color: '#6C5CE7' },
  { icon: 'bi-arrow-up-circle', label: 'Testosterone', color: '#FF6B6B' },
  { icon: 'bi-speedometer2', label: 'TRT', color: '#FDCB6E' },
  { icon: 'bi-bullseye', label: 'Peptides', color: '#00CEC9' },
];

const onboardingSteps = [
  { num: '01', title: 'Discovery Call', desc: '15-minute session. We learn your needs, you learn our platform.' },
  { num: '02', title: 'Custom Proposal', desc: 'Tailored plan with pricing, coverage options, and ROI projections.' },
  { num: '03', title: 'Setup & Onboarding', desc: 'We handle everything—portal setup, enrollment materials, training.' },
  { num: '04', title: 'Launch & Support', desc: 'Go live. Dedicated account manager. Quarterly reviews.' },
];

const employeeOptions = [
  { value: '1-25', label: '1\u201325' }, { value: '26-100', label: '26\u2013100' },
  { value: '101-500', label: '101\u2013500' }, { value: '501-1000', label: '501\u20131,000' },
  { value: '1000+', label: '1,000+' },
];

const modelOptions = [
  { value: 'member-paid', label: 'Member-Paid Perk', desc: 'Employees pay directly' },
  { value: 'sponsored', label: 'Sponsored Subsidy', desc: 'Company subsidizes' },
  { value: 'revenue-share', label: 'Revenue Share', desc: 'Earn per enrollment' },
];

const referralOptions = [
  { value: 'broker', label: 'Broker' }, { value: 'partner', label: 'Partner' },
  { value: 'search', label: 'Search' }, { value: 'referral', label: 'Referral' },
  { value: 'other', label: 'Other' },
];

const GroupsPage = () => {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [metricValues, setMetricValues] = useState({ enrolled: 0, savings: 0, satisfaction: 0 });
  const metricsCounted = useRef(false);
  const metricsRef = useRef(null);
  const stepInterval = useRef(null);
  const totalSteps = 3;

  const [form, setForm] = useState({
    company_name: '', contact_name: '', email: '', phone_number: '',
    employee_count: '', preferred_model: '', referral_source: ''
  });

  useEffect(() => {
    if (!document.querySelector('link[href*="bootstrap-icons"]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet'; link.href = 'https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.3/font/bootstrap-icons.min.css';
      document.head.appendChild(link);
    }
    document.body.style.background = '#FAF6F2';
    return () => { document.body.style.background = ''; };
  }, []);

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', h, { passive: true }); h();
    return () => window.removeEventListener('scroll', h);
  }, []);

  useEffect(() => {
    stepInterval.current = setInterval(() => setActiveStep(p => (p + 1) % 4), 3500);
    return () => clearInterval(stepInterval.current);
  }, []);

  useEffect(() => {
    const obs = new IntersectionObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('revealed'); obs.unobserve(e.target); } });
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });
    document.querySelectorAll('[data-reveal]').forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!metricsRef.current) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !metricsCounted.current) {
        metricsCounted.current = true;
        const targets = { enrolled: 10000, savings: 80, satisfaction: 97 };
        const duration = 2000; const start = performance.now();
        const tick = (now) => {
          const p = Math.min((now - start) / duration, 1);
          const ease = 1 - Math.pow(1 - p, 3);
          setMetricValues({ enrolled: Math.round(targets.enrolled * ease), savings: Math.round(targets.savings * ease), satisfaction: Math.round(targets.satisfaction * ease) });
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick); obs.disconnect();
      }
    }, { threshold: 0.3 });
    obs.observe(metricsRef.current);
    return () => obs.disconnect();
  }, []);

  const openModal = () => { setShowModal(true); document.documentElement.style.overflow = 'hidden'; };
  const closeModal = () => {
    setShowModal(false); document.documentElement.style.overflow = '';
    setTimeout(() => { setCurrentStep(1); setSubmitError(''); if (submitted) { setSubmitted(false); setForm({ company_name: '', contact_name: '', email: '', phone_number: '', employee_count: '', preferred_model: '', referral_source: '' }); } }, 300);
  };

  const formatPhone = (v) => { const d = v.replace(/\D/g, '').slice(0, 10); if (!d.length) return ''; if (d.length <= 3) return `(${d}`; if (d.length <= 6) return `(${d.slice(0,3)}) ${d.slice(3)}`; return `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`; };
  const onPhoneInput = (e) => { let r = e.target.value.replace(/\D/g, ''); if (r.length === 11 && r.startsWith('1')) r = r.slice(1); setForm(p => ({ ...p, phone_number: formatPhone(r.slice(0, 10)) })); if (errors.phone_number) setErrors(p => { const c = { ...p }; delete c.phone_number; return c; }); };

  const validateStep1 = () => {
    const e = {};
    if (!form.company_name.trim()) e.company_name = 'Company name is required';
    if (!form.contact_name.trim()) e.contact_name = 'Contact name is required';
    if (!form.email.trim()) e.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Enter a valid email';
    if (!form.phone_number.trim()) e.phone_number = 'Phone is required';
    else if (form.phone_number.replace(/\D/g, '').length !== 10) e.phone_number = 'Enter a valid 10-digit phone';
    setErrors(e); return Object.keys(e).length === 0;
  };
  const clearError = (f) => { if (errors[f]) setErrors(p => { const c = { ...p }; delete c[f]; return c; }); };

  const canProceed = (() => {
    if (currentStep === 1) return form.company_name.trim() && form.contact_name.trim() && form.email.trim() && form.phone_number.trim();
    if (currentStep === 2) return form.employee_count !== '';
    if (currentStep === 3) return form.preferred_model !== '';
    return false;
  })();

  const progressPercent = (currentStep / totalSteps) * 100;
  const nextStep = () => { if (currentStep === 1 && !validateStep1()) return; if (currentStep < totalSteps && canProceed) setCurrentStep(p => p + 1); };
  const prevStep = () => { if (currentStep > 1) setCurrentStep(p => p - 1); };

  // A refused application must never be swallowed: `fetch` resolves on 400/429/500, so the
  // response has to be read before anything is treated as accepted. Mirrors the sibling
  // public form's rule — the server's own message is the authority when it sends one.
  const submitForm = async () => {
    if (!canProceed) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      const res = await fetch('/api/forms/groups', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, phone_number: form.phone_number.replace(/\D/g, '') })
      });
      if (res.ok) setSubmitted(true);
      else {
        const data = await res.json().catch(() => ({}));
        setSubmitError(data?.error ?? 'We could not submit your application. Please try again.');
      }
    } catch (e) {
      console.error(e);
      setSubmitError('We could not reach the server. Please check your connection and try again.');
    }
    setSubmitting(false);
  };

  const selectStep = (i) => { setActiveStep(i); clearInterval(stepInterval.current); stepInterval.current = setInterval(() => setActiveStep(p => (p + 1) % 4), 3500); };

  return (
    <div className="groups-page">
      {/* NAV */}
      <nav className={`g-nav ${scrolled ? 'scrolled' : ''}`}>
        <div className="nav-inner">
          <Link to="/" className="brand"><img loading="lazy" src="/oval-logo.webp" alt="Oval" className="brand-logo" /></Link>
          <div className="nav-actions">
            <Link to="/login" className="nav-user-icon"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg></Link>
            <button className="nav-cta-btn" onClick={openModal}>Get Started</button>
            <button className="hamburger" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}><span className="line"/><span className="line"/><span className="line"/></button>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="g-hero">
        <div className="g-hero-bg"><div className="g-hero-gradient"/></div>
        <div className="g-hero-wrap">
          <div className="g-hero-left" data-reveal="true">
            <div className="g-hero-badge"><span className="g-badge-pulse"/>Now Available for Employers</div>
            <h1 className="g-hero-h1">Healthcare benefits<br/>your team will<br/><span className="g-hero-accent">actually use.</span></h1>
            <p className="g-hero-p">GLP-1s, telehealth, mental health, and specialty prescriptions—all at a fraction of the cost. Zero admin for HR.</p>
            <div className="g-hero-actions">
              <button className="g-btn-primary" onClick={openModal}>Get Started <i className="bi bi-arrow-right"/></button>
              <a href="#benefits" className="g-btn-ghost">Learn More <i className="bi bi-chevron-down"/></a>
            </div>
            <div className="g-hero-trust">
              <div className="g-trust-item"><i className="bi bi-shield-check"/> HIPAA Compliant</div>
              <div className="g-trust-item"><i className="bi bi-patch-check"/> LegitScript Certified</div>
              <div className="g-trust-item"><i className="bi bi-building"/> All 50 States</div>
            </div>
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section id="benefits" className="g-benefits">
        <div className="g-container">
          <div className="g-section-header" data-reveal="true">
            <span className="g-tag">Why Oval Access+</span>
            <h2 className="g-h2">Built for HR.<br/><span className="g-accent">Loved by employees.</span></h2>
            <p className="g-sub">Zero admin overhead. Full compliance. Real engagement.</p>
          </div>
          <div className="g-bento" data-reveal="true">
            {benefits.map((b, i) => (
              <div key={i} className="g-bento-card" style={{ transitionDelay: `${i * 0.1}s` }}>
                <div className="g-bento-icon"><i className={`bi ${b.icon}`}/></div>
                <h3 className="g-bento-title">{b.title}</h3>
                <p className="g-bento-desc">{b.desc}</p>
                <div className="g-bento-line"/>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OFFER MODELS */}
      <section className="g-models">
        <div className="g-container">
          <div className="g-section-header" data-reveal="true">
            <span className="g-tag light">Offer Models</span>
            <h2 className="g-h2 light">Three ways to bring Oval<br/><span className="g-accent-gradient">to your workforce</span></h2>
            <p className="g-sub light">Choose the model that fits your budget and culture.</p>
          </div>
          <div className="g-models-row" data-reveal="true">
            {offerModels.map((m, i) => (
              <div key={i} className={`g-model-card ${m.featured ? 'featured' : ''}`} style={{ transitionDelay: `${i * 0.12}s` }}>
                {m.featured && <div className="g-popular">Most Popular</div>}
                <div className="g-model-icon-wrap"><i className={`bi ${m.icon}`}/></div>
                <h3 className="g-model-name">{m.title}</h3>
                <p className="g-model-desc">{m.desc}</p>
                <ul className="g-model-list">
                  {m.highlights.map((h, j) => <li key={j}><svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M20 6L9 17l-5-5" stroke="#FF7A00" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>{h}</li>)}
                </ul>
                <button className={`g-model-btn ${m.featured ? 'featured-btn' : ''}`} onClick={openModal}>Select Model <i className="bi bi-arrow-right"/></button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* EMPLOYEE BENEFITS MARQUEE */}
      <section className="g-coverage">
        <div className="g-container">
          <div className="g-coverage-top" data-reveal="true">
            <div className="g-coverage-text">
              <span className="g-tag">Employee Benefits</span>
              <h2 className="g-h2">Coverage for what insurance<br/><span className="g-accent">doesn't cover</span></h2>
              <p className="g-sub">From weight management to mental wellness, employees get access to medications normally out-of-pocket—at a fraction of the cost.</p>
            </div>
          </div>
          <div className="g-benefit-marquee" data-reveal="true">
            <div className="g-marquee-track">
              <div className="g-marquee-content">
                {employeeBenefits.map((cat, i) => <div key={`a-${i}`} className="g-benefit-pill" style={{ '--accent': cat.color }}><div className="g-pill-icon"><i className={`bi ${cat.icon}`}/></div><span className="g-pill-label">{cat.label}</span></div>)}
              </div>
              <div className="g-marquee-content" aria-hidden="true">
                {employeeBenefits.map((cat, i) => <div key={`b-${i}`} className="g-benefit-pill" style={{ '--accent': cat.color }}><div className="g-pill-icon"><i className={`bi ${cat.icon}`}/></div><span className="g-pill-label">{cat.label}</span></div>)}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* METRICS */}
      <section className="g-metrics" ref={metricsRef}>
        <div className="g-container">
          <div className="g-metrics-row" data-reveal="true">
            <div className="g-metric"><span className="g-metric-num">{metricValues.enrolled.toLocaleString()}+</span><span className="g-metric-label">Members Enrolled</span></div>
            <div className="g-metric-divider"/>
            <div className="g-metric"><span className="g-metric-num">{metricValues.savings}%</span><span className="g-metric-label">Avg. Rx Savings</span></div>
            <div className="g-metric-divider"/>
            <div className="g-metric"><span className="g-metric-num">{metricValues.satisfaction}%</span><span className="g-metric-label">Member Satisfaction</span></div>
          </div>
        </div>
      </section>

      {/* IMPACT */}
      <section className="g-impact">
        <div className="g-impact-bg"><div className="g-impact-glow g-impact-glow--1"/><div className="g-impact-glow g-impact-glow--2"/></div>
        <div className="g-container">
          <div className="g-impact-layout" data-reveal="true">
            <div className="g-impact-left">
              <div className="g-impact-tag"><span className="g-impact-tag-dot"/>The Oval Difference</div>
              <blockquote className="g-impact-quote">A benefit employees<br/><span className="g-impact-highlight">choose</span> to use.</blockquote>
              <p className="g-impact-sub">That's the difference between a checkbox and a competitive advantage.</p>
            </div>
            <div className="g-impact-right">
              <div className="g-impact-stats">
                <div className="g-impact-stat"><span className="g-impact-stat-num">4.9<span className="g-impact-stat-unit">/5</span></span><span className="g-impact-stat-label">Average member rating</span><div className="g-impact-stars"><i className="bi bi-star-fill"/><i className="bi bi-star-fill"/><i className="bi bi-star-fill"/><i className="bi bi-star-fill"/><i className="bi bi-star-fill"/></div></div>
                <div className="g-impact-divider"/>
                <div className="g-impact-stat"><span className="g-impact-stat-num">3x</span><span className="g-impact-stat-label">Higher engagement than traditional benefits</span></div>
                <div className="g-impact-divider"/>
                <div className="g-impact-stat"><span className="g-impact-stat-num">72<span className="g-impact-stat-unit">%</span></span><span className="g-impact-stat-label">of employees enroll within 30 days</span></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ONBOARDING TIMELINE */}
      <section id="how-it-works" className="g-onboarding">
        <div className="g-container">
          <div className="g-section-header" data-reveal="true">
            <span className="g-tag">How It Works</span>
            <h2 className="g-h2">From conversation to<br/><span className="g-accent">coverage in 4 steps</span></h2>
          </div>
          <div className="g-timeline-track" data-reveal="true">
            <div className="g-timeline-connector"><div className="g-timeline-connector-fill" style={{ width: `${(activeStep / 3) * 100}%` }}/></div>
            <div className="g-timeline-nodes">
              {onboardingSteps.map((s, i) => (
                <div key={i} className={`g-timeline-node ${activeStep === i ? 'g-node-active' : ''} ${activeStep > i ? 'g-node-done' : ''}`} onClick={() => selectStep(i)}>
                  <div className="g-node-dot">
                    {activeStep > i ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg> : <span className="g-node-num">{s.num}</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="g-steps-row" data-reveal="true">
            {onboardingSteps.map((s, i) => (
              <div key={i} className={`g-step-card ${activeStep === i ? 'g-step-active' : ''} ${activeStep > i ? 'g-step-done' : ''}`} onClick={() => selectStep(i)}>
                <div className="g-step-content"><span className="g-step-num">{s.num}</span><h3 className="g-step-title">{s.title}</h3><p className="g-step-desc">{s.desc}</p></div>
                <div className="g-step-progress"><div className="g-step-progress-fill"/></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="g-cta">
        <div className="g-container">
          <div className="g-cta-card" data-reveal="true">
            <div className="g-cta-content">
              <h2 className="g-cta-h2">Ready to upgrade your employee benefits?</h2>
              <p className="g-cta-p">Join forward-thinking employers offering healthcare benefits that employees actually value.</p>
              <button className="g-btn-primary large" onClick={openModal}>Get Started Today <i className="bi bi-arrow-right"/></button>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="partners-footer" data-reveal="true">
        <div className="g-container">
          <div className="footer-top">
            <div className="footer-brand"><img loading="lazy" src="/oval-logo.webp" alt="Oval" className="footer-logo" style={{ filter: 'brightness(0) invert(1)' }}/><p className="footer-tagline">Modern healthcare benefits for modern employers.</p></div>
            <nav className="footer-links"><Link to="/terms">Terms & Conditions</Link><Link to="/privacy">Privacy Policy</Link><Link to="/hipaa">HIPAA Notice</Link><Link to="/states">States We Serve</Link></nav>
            <div className="footer-cert"><span className="footer-cert-label">Certified by:</span><a href="https://www.legitscript.com/websites/ovalcare.com" target="_blank" rel="noopener noreferrer"><img loading="lazy" src="/images/legit-script.webp" alt="LegitScript" className="footer-cert-img"/></a></div>
          </div>
          <div className="footer-bottom"><span>&copy; {new Date().getFullYear()} Oval Care. All rights reserved.</span></div>
        </div>
      </footer>

      {/* MOBILE CTA */}
      <div className={`g-mobile-cta ${scrolled ? 'visible' : ''}`}>
        <button className="g-btn-primary full" onClick={openModal}>Get Started <i className="bi bi-arrow-right"/></button>
      </div>

      {/* MODAL */}
      {showModal && (
        <div className="g-overlay" onClick={e => { if (e.target === e.currentTarget) closeModal(); }}>
          <div className="g-modal">
            <button className="g-modal-close" onClick={closeModal}><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>
            <div className="g-modal-progress"><div className="g-modal-bar" style={{ width: `${progressPercent}%` }}/></div>
            {!submitted ? (
              <div className="g-modal-body">
                <div className="g-modal-step-label">Step {currentStep} of {totalSteps}</div>
                {currentStep === 1 && (
                  <div className="g-step-panel">
                    <h3 className="g-modal-h3">Tell us about your company</h3>
                    <p className="g-modal-sub">We'll use this to set up your account.</p>
                    <div className={`g-form-group ${errors.company_name ? 'has-error' : ''}`}>
                      <label>Company Name</label>
                      <input value={form.company_name} onChange={e => { setForm(p => ({ ...p, company_name: e.target.value })); clearError('company_name'); }} placeholder="Acme Corp" />
                      {errors.company_name && <span className="g-err">{errors.company_name}</span>}
                    </div>
                    <div className={`g-form-group ${errors.contact_name ? 'has-error' : ''}`}>
                      <label>Your Name</label>
                      <input value={form.contact_name} onChange={e => { setForm(p => ({ ...p, contact_name: e.target.value })); clearError('contact_name'); }} placeholder="Jane Smith" />
                      {errors.contact_name && <span className="g-err">{errors.contact_name}</span>}
                    </div>
                    <div className="g-form-row">
                      <div className={`g-form-group ${errors.email ? 'has-error' : ''}`}>
                        <label>Email</label>
                        <input value={form.email} onChange={e => { setForm(p => ({ ...p, email: e.target.value })); clearError('email'); }} placeholder="jane@acme.com" type="email" />
                        {errors.email && <span className="g-err">{errors.email}</span>}
                      </div>
                      <div className={`g-form-group ${errors.phone_number ? 'has-error' : ''}`}>
                        <label>Phone</label>
                        <input value={form.phone_number} onChange={onPhoneInput} placeholder="(555) 123-4567" type="tel" maxLength="14" />
                        {errors.phone_number && <span className="g-err">{errors.phone_number}</span>}
                      </div>
                    </div>
                  </div>
                )}
                {currentStep === 2 && (
                  <div className="g-step-panel">
                    <h3 className="g-modal-h3">How many employees?</h3>
                    <p className="g-modal-sub">This helps us tailor the right offer.</p>
                    <div className="g-chips">
                      {employeeOptions.map(o => <button key={o.value} className={`g-chip ${form.employee_count === o.value ? 'selected' : ''}`} onClick={() => setForm(p => ({ ...p, employee_count: o.value }))}>{o.label}</button>)}
                    </div>
                  </div>
                )}
                {currentStep === 3 && (
                  <div className="g-step-panel">
                    <h3 className="g-modal-h3">Which model interests you?</h3>
                    <p className="g-modal-sub">You can always change this later.</p>
                    <div className="g-model-chips">
                      {modelOptions.map(o => <button key={o.value} className={`g-model-chip ${form.preferred_model === o.value ? 'selected' : ''}`} onClick={() => setForm(p => ({ ...p, preferred_model: o.value }))}><span className="g-mc-label">{o.label}</span><span className="g-mc-desc">{o.desc}</span></button>)}
                    </div>
                    <div className="g-referral-section">
                      <label className="g-referral-label">How did you hear about us? <span className="g-optional">(Optional)</span></label>
                      <div className="g-chips small">
                        {referralOptions.map(o => <button key={o.value} className={`g-chip small ${form.referral_source === o.value ? 'selected' : ''}`} onClick={() => setForm(p => ({ ...p, referral_source: p.referral_source === o.value ? '' : o.value }))}>{o.label}</button>)}
                      </div>
                    </div>
                  </div>
                )}
                {submitError && (
                  <div className="g-form-group has-error">
                    <span className="g-err" role="alert">{submitError}</span>
                  </div>
                )}
                <div className="g-modal-nav">
                  {currentStep > 1 ? <button className="g-nav-back" onClick={prevStep}><i className="bi bi-arrow-left"/> Back</button> : <div/>}
                  {currentStep < totalSteps ? (
                    <button className="g-nav-next" disabled={!canProceed} onClick={nextStep}>Continue <i className="bi bi-arrow-right"/></button>
                  ) : (
                    <button className="g-nav-submit" disabled={!canProceed || submitting} onClick={submitForm}>
                      {submitting ? <><span className="g-spinner"/> Submitting...</> : <>Submit Application <i className="bi bi-check-lg"/></>}
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="g-success">
                <div className="g-success-icon"><i className="bi bi-check-lg"/></div>
                <h2 className="g-success-h2">Application Submitted!</h2>
                <p className="g-success-p">Thanks {form.contact_name.split(' ')[0]}! Our team will reach out within <strong>24 hours</strong> to schedule your discovery call.</p>
                <button className="g-btn-primary" onClick={closeModal}>Got It</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GroupsPage;
